import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/money";
import { Icon } from "@/components/Icon";
import CardForm from "./CardForm";
import BrandMark from "./BrandMark";

/*
  PRYMEBET branded in-app checkout — pixel-faithful port of the Claude Design mockups
  (.design-export/PNRCheckoutSheet.dc.html + .design-export/04b Checkout.dc.html).

  Replaces the redirect to Stripe's hosted page. Four states driven by `step`:
    method (CK-01) -> pay (CK-02) -> success (CK-03) | failed (CK-04)

  Framing: desktop = 520px centered modal over a dimmed/blurred backdrop;
  mobile (< lg) = bottom-sheet with a grab handle. Rendered through a portal
  onto document.body so it overlays the whole app.

  Card is the functional path: it opens a Stripe PaymentIntent, renders our own
  Stripe Elements card fields (styled to PRYMEBET), confirms the card, then polls
  /payments/intent/{id} to credit and refreshes the auth user. Card is the ONLY
  usable method today, so it is the single active choice and Continue always
  progresses to the card step. Other methods (Apple/Google Pay express, PayPal,
  Venmo, Crypto) are shown as DISABLED tiles with a visible "Coming
  soon" badge — they are not selectable and never make Continue a no-op, so we
  never imply they work.
*/

// Design constants (colors, from the .dc.html)
const GREEN = "#00D632";
const RED = "#EF4444";
const CO = "#0A0A0A"; // green-button text / on-green

// BOLD UI §2.2 — premium panel depth for the inline-styled order/summary panels: a directional
// ink→charcoal gradient + a hairline top-light + grounding, so these inner panels read machined
// and raised rather than a flat #111114 fill. Presentation only (matches the .surface-premium recipe).
const PREMIUM_PANEL = {
  background: "linear-gradient(145deg, #141419 0%, #141417 45%, #0D0D11 100%)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.07), inset 0 -1px 0 rgba(0,0,0,.55)",
  border: "1px solid #1F1F23",
};

const fmtInt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

// Non-card alternate methods, exactly as the mockup's `methods` array (CK-01 grid).
const ALT_METHODS = [
  { key: "paypal", name: "PayPal", showLabel: false, logos: [["paypal", "PayPal"]] },
  { key: "venmo", name: "Venmo", showLabel: false, logos: [["venmo", "Venmo"]] },
  {
    key: "crypto",
    name: "Crypto",
    showLabel: true,
    logos: [["bitcoin", "Bitcoin"], ["ethereum", "Ethereum"], ["tether", "Tether USDT"]],
  },
];

/**
 * CheckoutSheet
 * @param {boolean}  open      whether the sheet is shown
 * @param {object}   order     { lookup_key, custom_usd?, label, usd, coins, cash,
 *                               firstBonusAvailable, firstBonusCoins, firstBonusCash }
 * @param {string}   promoId   optional chosen promo id
 * @param {function} onClose   close the sheet
 */
export default function CheckoutSheet({ open, order, promoId = null, onClose }) {
  const { user, refresh, streamerMode } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("method"); // method | pay | success | failed

  // Stripe intent state
  const [creating, setCreating] = useState(false);
  const [intent, setIntent] = useState(null); // server /payments/intent response
  const [stripePromise, setStripePromise] = useState(null);
  const [errorMsg, setErrorMsg] = useState(""); // failed-state message

  // Result state (success)
  const [credit, setCredit] = useState(null); // { coins, cash, first_purchase, bonus_coins, bonus_cash }
  const [newUser, setNewUser] = useState(null); // refreshed auth user for the balance
  const [card, setCard] = useState(null); // { brand, last4 } of the paid/declined card
  const [cardDeclined, setCardDeclined] = useState(false); // true only for a CONFIRMED card decline
  const [verified, setVerified] = useState(true); // false => payment taken but credit not yet server-confirmed

  // Focus management (UIA-14): remember the opener, contain Tab, restore on close.
  const dialogRef = useRef(null);
  const openerRef = useRef(null);

  // Reset when (re)opened
  useEffect(() => {
    if (open) {
      setStep("method");
      setIntent(null);
      setStripePromise(null);
      setErrorMsg("");
      setCredit(null);
      setNewUser(null);
      setCard(null);
      setCardDeclined(false);
    }
  }, [open, order?.lookup_key, order?.usd]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc closes + focus trap (UIA-14). Tab / Shift+Tab are contained within the
  // dialog; Escape closes. Return focus to the opener is handled on unmount below.
  useEffect(() => {
    if (!open) return;
    // Remember what had focus so we can restore it when the sheet closes.
    openerRef.current = document.activeElement;

    const focusables = () => {
      const root = dialogRef.current;
      if (!root) return [];
      return Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el.getClientRects().length > 0);
    };

    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key !== "Tab") return;
      const nodes = focusables();
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      const root = dialogRef.current;
      // If focus escaped the dialog entirely (e.g. into a Stripe iframe boundary),
      // pull it back to an edge rather than leaking to the page behind the modal.
      if (!root || !root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      // Return focus to the element that opened the sheet.
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function") {
        // defer so it runs after the portal is torn down
        setTimeout(() => { try { opener.focus(); } catch (_) {} }, 0);
      }
    };
  }, [open, onClose]);

  // Initial focus — move focus to the first field/primary action once rendered per step.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const root = dialogRef.current;
      if (!root) return;
      // Prefer the step's primary action; fall back to the first focusable.
      const primary = root.querySelector(
        '[data-testid="checkout-continue"], [data-testid="checkout-pay"], [data-testid="checkout-try-again"], [data-testid="checkout-start-playing"]'
      );
      const target = primary || root.querySelector('button:not([disabled]), a[href], input:not([disabled])');
      if (target && typeof target.focus === "function") { try { target.focus(); } catch (_) {} }
    }, 40);
    return () => clearTimeout(t);
  }, [open, step, stripePromise]);

  // Derived order display values (never hardcoded — comes from the selected pack/custom)
  const view = useMemo(() => {
    const o = order || {};
    return {
      label: o.label || "Coins",
      usd: Number(o.usd || 0),
      coins: Number(o.coins || 0),
      cash: Number(o.cash || 0),
      firstBonusAvailable: !!o.firstBonusAvailable,
      firstBonusCoins: Number(o.firstBonusCoins || 0),
      firstBonusCash: Number(o.firstBonusCash || 0),
    };
  }, [order]);

  // --- create intent + load Stripe when moving method -> pay with Card ---
  const startCardPayment = useCallback(async () => {
    if (!order?.lookup_key) return;
    setCreating(true);
    setErrorMsg("");
    try {
      const { data } = await api.post("/payments/intent", {
        lookup_key: order.lookup_key,
        custom_usd: order.custom_usd ?? null,
        promo_id: promoId || null,
      });
      if (!data.publishable_key) {
        setErrorMsg("Card payments are not configured. Please contact support.");
        setStep("failed");
        return;
      }
      setIntent(data);
      setStripePromise(loadStripe(data.publishable_key));
      setStep("pay");
    } catch (e) {
      setErrorMsg(apiError(e?.response?.data?.detail) || "Could not start checkout. Please try again.");
      setStep("failed");
    } finally {
      setCreating(false);
    }
  }, [order, promoId]);

  // Card is the only usable method, so Continue always starts the card flow.
  const handleContinue = () => {
    startCardPayment();
  };

  // Called by CardForm after stripe.confirmCardPayment succeeds. Stripe authorizing the card is NOT
  // proof the backend credited the coins, so we only claim "added" if at least one server read
  // confirms it; otherwise we show an honest "payment received, balance updating" state. (The backend
  // credit is idempotent via the Stripe webhook, so the coins still land even if these reads fail.)
  const handlePaid = useCallback(async (paymentIntentId) => {
    let confirmed = false;
    try {
      const { data } = await api.get(`/payments/intent/${paymentIntentId}`);
      if (data) { setCredit(data); confirmed = true; }
      if (data?.card_brand || data?.card_last4) setCard({ brand: data.card_brand, last4: data.card_last4 });
    } catch { /* fall through to the balance refresh */ }
    try {
      const u = await refresh();
      if (u) { setNewUser(u); confirmed = true; }
    } catch { /* ignore */ }
    setVerified(confirmed);
    setStep("success");
  }, [refresh]);

  // Called by CardForm when the card is declined / errors.
  //   declined = true  -> a CONFIRMED card decline (safe to say "declined by your bank")
  //   declined = false -> a general/validation/processing error (use the generic message)
  const handleCardError = useCallback((msg, cardInfo, declined = false) => {
    setErrorMsg(msg || (declined
      ? "Your card was declined. You were not charged."
      : "We couldn't complete this payment. You were not charged."));
    if (cardInfo && (cardInfo.brand || cardInfo.last4)) setCard(cardInfo);
    setCardDeclined(!!declined);
    setStep("failed");
  }, []);

  if (!open) return null;

  // Paid/declined card brand + last4 for the success + declined labels ("Visa •••• 4242").
  const cardBrandKey = card?.brand && ["visa", "mastercard", "amex"].includes(card.brand) ? card.brand : null;
  const cardBrandLabel = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover", diners: "Diners", jcb: "JCB", unionpay: "UnionPay" }[card?.brand] || "Card";
  const cardLast4 = card?.last4 || null;

  // ---------- shared building blocks (styles mirror the .dc.html inline styles) ----------

  const closeBtn = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close checkout"
      data-testid="checkout-close"
      style={{
        width: 40, height: 40, flex: "none", borderRadius: 12, background: "#18181C",
        boxShadow: "inset 0 0 0 1px #27272A", display: "flex", alignItems: "center",
        justifyContent: "center", border: "none", cursor: "pointer", padding: 0,
      }}
    >
      <Icon name="x" size={16} style={{ color: "#a1a1aa" }} />
    </button>
  );

  const secureLine = (
    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: "600 11px 'Outfit'", color: "#71717a" }}>
      <Icon name="lock" size={12} style={{ color: "#71717a" }} />
      Secure checkout · powered by Stripe · 18+
    </span>
  );

  const legalLine = (
    <span style={{ font: "500 11px/1.5 'Outfit'", color: "#71717a" }}>
      No purchase necessary. PCoins for play; PCash redeemable per the{" "}
      <span style={{ color: "#a1a1aa", textDecoration: "underline" }}>Sweepstakes Rules</span>.
    </span>
  );

  // -------------------------------- STATE: method (CK-01) --------------------------------
  const methodStep = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Outfit',sans-serif", color: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "800 20px/1.1 'Manrope'", letterSpacing: "-.02em" }}>Checkout</div>
          <div style={{ marginTop: 3, font: "500 12px 'Outfit'", color: "#71717a" }}>Step 1 of 2 · Payment method</div>
        </div>
        {closeBtn}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <span style={{ height: 3, borderRadius: 999, background: GREEN }} />
        <span style={{ height: 3, borderRadius: 999, background: "#27272A" }} />
      </div>

      {/* order line */}
      <div style={{ borderRadius: 14, ...PREMIUM_PANEL }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
          <Icon name="pnrcoin" size={28} style={{ color: GREEN }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "700 15px 'Outfit'" }}>{view.label}</div>
            <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 5, font: "500 12px 'Outfit'", color: "#a1a1aa", whiteSpace: "nowrap", minWidth: 0 }}>
              <span style={{ flex: "none" }}>{fmtInt(view.coins)} coins</span>
              <span style={{ flex: "none", color: "#52525b" }}>+</span>
              <Icon name="pnrcash" size={12} style={{ color: "#E4E4E7" }} />
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{fmtUsd(view.cash)} free cash</span>
            </div>
          </div>
          <span style={{ font: "800 18px 'Manrope'", letterSpacing: "-.02em" }}>{fmtUsd(view.usd)}</span>
        </div>
        {view.firstBonusAvailable && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderTop: "1px solid #1F1F23" }}>
            <Icon name="celebrate" size={14} style={{ color: GREEN }} />
            <span style={{ flex: 1, font: "600 12px 'Outfit'", color: "#d4d4d8" }}>First-purchase bonus applied</span>
            <span style={{ font: "600 12px 'Outfit'", color: "#a1a1aa", whiteSpace: "nowrap" }}>+{fmtInt(view.firstBonusCoins)} · +{fmtUsd(view.firstBonusCash)}</span>
          </div>
        )}
      </div>

      {/* express — not wired yet; shown disabled with a visible "coming soon" badge so we never imply they work */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ font: "700 10px 'Outfit'", letterSpacing: ".16em", color: "#71717a" }}>EXPRESS · COMING SOON</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["apple-pay", "Apple Pay"], ["google-pay", "Google Pay"]].map(([brand, alt]) => (
            <div key={brand} data-testid={`checkout-${brand === "apple-pay" ? "applepay" : "googlepay"}`}
              aria-disabled="true"
              style={{ position: "relative", height: 52, borderRadius: 12, background: "#000", boxShadow: "inset 0 0 0 1px #27272A", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.45 }}>
              <BrandMark brand={brand} alt={`${alt} — coming soon`} height={22} />
              <span style={{ position: "absolute", top: 6, right: 8, font: "700 8px 'Outfit'", letterSpacing: ".08em", color: "#71717a", textTransform: "uppercase" }}>Soon</span>
            </div>
          ))}
        </div>
      </div>

      {/* divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ flex: 1, height: 1, background: "#1F1F23" }} />
        <span style={{ font: "600 12px 'Outfit'", color: "#71717a" }}>or pay with</span>
        <span style={{ flex: 1, height: 1, background: "#1F1F23" }} />
      </div>

      {/* method list — Card is the only usable method (always selected); the rest are disabled "coming soon" tiles */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Card (the real, always-selected method) */}
        <div data-testid="checkout-method-card" role="radio" aria-checked="true"
          style={{
            height: 60, boxSizing: "border-box", padding: "0 14px", borderRadius: 14,
            background: "rgba(0,214,50,.06)", boxShadow: "inset 0 0 0 1.5px #00D632",
            display: "flex", alignItems: "center", gap: 10, width: "100%",
          }}>
          <span style={{ font: "700 15px 'Outfit'", color: "#fff" }}>Card</span>
          <div style={{ display: "flex", gap: 5, marginLeft: "auto" }}>
            <BrandMark brand="visa" alt="Visa" height={24} />
            <BrandMark brand="mastercard" alt="Mastercard" height={24} />
            <BrandMark brand="amex" alt="American Express" height={24} />
          </div>
          <span style={radioStyle(true)} />
        </div>

        {/* Alternate methods grid — disabled, visibly "coming soon", no radio, not selectable */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {ALT_METHODS.map((m) => (
            <div key={m.key} data-testid={`checkout-method-${m.key}`} aria-disabled="true"
              style={{
                position: "relative", height: 60, boxSizing: "border-box", padding: "0 14px", borderRadius: 14,
                background: "#111114", boxShadow: "inset 0 0 0 1px #27272A",
                display: "flex", alignItems: "center", gap: 10, minWidth: 0, opacity: 0.5,
              }}>
              <div style={{ display: "flex", alignItems: "center", paddingRight: 5, flex: "none" }}>
                {m.logos.map(([src, alt]) => (
                  <BrandMark key={src} brand={src} alt={`${alt} — coming soon`} height={22}
                    style={{ marginRight: -5, borderRadius: 999, boxShadow: "0 0 0 2px #111114" }} />
                ))}
              </div>
              {m.showLabel && <span style={{ font: "600 14px 'Outfit'", whiteSpace: "nowrap", color: "#fff" }}>{m.name}</span>}
              <span style={{ marginLeft: "auto", font: "700 9px 'Outfit'", letterSpacing: ".06em", color: "#71717a", textTransform: "uppercase", whiteSpace: "nowrap" }}>Soon</span>
            </div>
          ))}
        </div>
      </div>

      <span data-testid="checkout-coming-soon" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: "600 12px 'Outfit'", color: "#a1a1aa", marginTop: -4 }}>
        <Icon name="info" size={13} style={{ color: "#71717a" }} />
        More payment methods are coming soon — pay with Card to complete your purchase today.
      </span>

      {/* Continue — always progresses to the card step (card is the only method) */}
      <button type="button" onClick={handleContinue} disabled={creating} data-testid="checkout-continue"
        style={{ ...greenCta(56, "800 17px 'Manrope'"), opacity: creating ? 0.7 : 1 }}>
        {creating ? "Starting…" : "Continue"}
      </button>
      {secureLine}
    </div>
  );

  // ---------------------------------- STATE: pay (CK-02) ---------------------------------
  const payStep = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Outfit',sans-serif", color: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
        <button type="button" onClick={() => setStep("method")} aria-label="Back" data-testid="checkout-back"
          style={{ width: 40, height: 40, flex: "none", borderRadius: 12, background: "#18181C", boxShadow: "inset 0 0 0 1px #27272A", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", padding: 0 }}>
          <Icon name="chevron-left" size={18} style={{ color: "#fff" }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "800 20px/1.1 'Manrope'", letterSpacing: "-.02em" }}>Pay</div>
          <div style={{ marginTop: 3, font: "500 12px 'Outfit'", color: "#71717a" }}>Step 2 of 2 · Confirm and pay</div>
        </div>
        {closeBtn}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <span style={{ height: 3, borderRadius: 999, background: GREEN }} />
        <span style={{ height: 3, borderRadius: 999, background: GREEN }} />
      </div>

      {/* order summary */}
      <div style={{ borderRadius: 14, ...PREMIUM_PANEL, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 9 }}>
        <div style={{ display: "flex", justifyContent: "space-between", font: "700 14px 'Outfit'" }}>
          <span>{view.label}</span><span>{fmtUsd(view.usd)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
          <span>PCoins</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#fff", fontWeight: 700 }}>
            <Icon name="pnrcoin" size={14} style={{ color: GREEN }} />{fmtInt(view.coins)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
          <span>Free PCash <span style={{ color: "#52525b" }}>· redeemable 1:1</span></span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#fff", fontWeight: 700 }}>
            <Icon name="pnrcash" size={14} style={{ color: "#E4E4E7" }} />{fmtUsd(view.cash)}
          </span>
        </div>
        {view.firstBonusAvailable && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
            <span>First-purchase bonus</span>
            <span style={{ color: GREEN, fontWeight: 700, whiteSpace: "nowrap" }}>+{fmtInt(view.firstBonusCoins)} · +{fmtUsd(view.firstBonusCash)}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 10, borderTop: "1px solid #1F1F23" }}>
          <span style={{ font: "700 14px 'Outfit'" }}>Total</span>
          <span style={{ font: "800 24px/1 'Manrope'", letterSpacing: "-.03em" }}>
            {fmtUsd(view.usd)} <span style={{ font: "600 11px 'Outfit'", color: "#71717a", letterSpacing: 0 }}>USD</span>
          </span>
        </div>
      </div>

      {/* Stripe Elements card fields (our own design) */}
      {stripePromise && intent ? (
        <Elements
          stripe={stripePromise}
          options={{
            appearance: {
              theme: "night",
              variables: {
                colorPrimary: GREEN,
                colorBackground: "#111114",
                colorText: "#ffffff",
                colorDanger: RED,
                fontFamily: "Outfit, sans-serif",
                borderRadius: "12px",
              },
            },
          }}
        >
          <CardForm
            amountUsd={intent.amount_usd ?? view.usd}
            clientSecret={intent.client_secret}
            paymentIntentId={intent.payment_intent_id}
            onPaid={handlePaid}
            onError={handleCardError}
          />
        </Elements>
      ) : (
        <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#71717a", font: "600 13px 'Outfit'" }}>
          <Icon name="refresh" size={18} className="animate-spin" style={{ color: GREEN, marginRight: 8 }} /> Loading secure card form…
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 4, textAlign: "center" }}>
        <span style={{ font: "600 11px 'Outfit'", color: "#a1a1aa" }}>Secure checkout · powered by Stripe · 18+</span>
        {legalLine}
      </div>
    </div>
  );

  // -------------------------------- STATE: failed (CK-04) --------------------------------
  const failedStep = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Outfit',sans-serif", color: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{closeBtn}</div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center", marginTop: -18 }}>
        <span style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1.5px #EF4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="alert-circle" size={28} style={{ color: RED }} />
        </span>
        <div style={{ font: "800 28px/1.05 'Manrope'", letterSpacing: "-.035em" }}>Payment didn't go through</div>
        <div style={{ font: "500 14px/1.5 'Outfit'", color: "#a1a1aa" }}>{errorMsg || "Your card was declined. You were not charged."}</div>
        <span style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 12px", borderRadius: 999, background: "#18181C", boxShadow: "inset 0 0 0 1px #27272A", font: "600 12px 'Outfit'", color: "#d4d4d8" }}>
          <span style={{ width: 6, height: 6, flex: "none", borderRadius: "50%", background: RED }} />
          No charge made · No PCoins added
        </span>
      </div>

      {/* order line (greyed) */}
      <div style={{ borderRadius: 14, ...PREMIUM_PANEL }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
          <Icon name="pnrcoin" size={28} style={{ color: "#52525b" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "700 15px 'Outfit'" }}>{view.label}</div>
            <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 5, font: "500 12px 'Outfit'", color: "#a1a1aa", whiteSpace: "nowrap", minWidth: 0 }}>
              <span style={{ flex: "none" }}>{fmtInt(view.coins)} coins</span>
              <span style={{ flex: "none", color: "#52525b" }}>+</span>
              <Icon name="pnrcash" size={12} style={{ color: "#a1a1aa" }} />
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{fmtUsd(view.cash)} free cash</span>
            </div>
          </div>
          <span style={{ font: "800 18px 'Manrope'", letterSpacing: "-.02em" }}>{fmtUsd(view.usd)}</span>
        </div>
        {view.firstBonusAvailable && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderTop: "1px solid #1F1F23" }}>
            <Icon name="celebrate" size={14} style={{ color: "#71717a" }} />
            <span style={{ flex: 1, font: "600 12px 'Outfit'", color: "#a1a1aa" }}>First-purchase bonus · still available</span>
            <span style={{ font: "600 12px 'Outfit'", color: "#71717a", whiteSpace: "nowrap" }}>+{fmtInt(view.firstBonusCoins)} · +{fmtUsd(view.firstBonusCash)}</span>
          </div>
        )}
      </div>

      {/* declined card chip */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, height: 56, boxSizing: "border-box", padding: "0 14px", borderRadius: 14, background: "rgba(239,68,68,.06)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>
        <BrandMark brand={cardBrandKey || "visa"} alt={cardBrandLabel} height={26} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "700 14px 'Outfit'" }}>{cardLast4 ? `${cardBrandLabel} •••• ${cardLast4}` : "Card payment"}</div>
          <div style={{ font: "500 11px 'Outfit'", color: RED }}>Declined by your bank</div>
        </div>
        <Icon name="alert-circle" size={18} style={{ color: RED }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button type="button" onClick={() => { setErrorMsg(""); setStep("pay"); }} data-testid="checkout-try-again"
          style={greenCta(56, "800 17px 'Manrope'", true, "refresh")}>
          <Icon name="refresh" size={16} style={{ color: CO }} /> Try again
        </button>
        <button type="button" onClick={() => { setErrorMsg(""); setStep("method"); }} data-testid="checkout-another-method"
          style={{ height: 52, borderRadius: 14, background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", display: "flex", alignItems: "center", justifyContent: "center", font: "700 15px 'Outfit'", color: "#fff", border: "none", cursor: "pointer", width: "100%" }}>
          Use another method
        </button>
        <button type="button" onClick={() => { onClose?.(); navigate("/support"); }} data-testid="checkout-support"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minHeight: 32, font: "600 13px 'Outfit'", color: "#d4d4d8", background: "none", border: "none", cursor: "pointer" }}>
          <Icon name="support" size={14} style={{ color: "#a1a1aa" }} />
          <span style={{ textDecoration: "underline" }}>Contact support</span>
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, textAlign: "center" }}>
        <span style={{ font: "600 11px 'Outfit'", color: "#a1a1aa" }}>Secure checkout · powered by Stripe · 18+</span>
        {legalLine}
        {intent?.payment_intent_id && (
          <span style={{ marginTop: 4, font: "500 11px 'Outfit'", color: "#71717a" }}>Support reference · {refShort(intent.payment_intent_id)}</span>
        )}
      </div>
    </div>
  );

  // -------------------------------- STATE: success (CK-03) -------------------------------
  const credited = credit || {};
  const creditedCoins = credited.coins != null ? credited.coins : view.coins;
  const creditedCash = credited.cash != null ? credited.cash : view.cash;
  const bonusCoins = credited.bonus_coins || (view.firstBonusAvailable ? view.firstBonusCoins : 0);
  const bonusCash = credited.bonus_cash || (view.firstBonusAvailable ? view.firstBonusCash : 0);
  const showBonus = (credited.first_purchase ?? view.firstBonusAvailable) && (bonusCoins > 0 || bonusCash > 0);
  const balUser = newUser || user || {};

  const successStep = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Outfit',sans-serif", color: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{closeBtn}</div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center", marginTop: -18 }}>
        <span style={{ width: 60, height: 60, borderRadius: "50%", background: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="check" size={28} style={{ color: CO }} />
        </span>
        <div style={{ font: "800 28px/1.05 'Manrope'", letterSpacing: "-.035em" }}>{verified ? "Coins added" : "Payment received"}</div>
        {!verified && (
          <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa", maxWidth: 300 }}>
            Your payment went through. Your coins will appear in your wallet shortly — pull to refresh if they don't show right away.
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
          <span>{view.label} · {fmtUsd(view.usd)} ·</span>
          {cardBrandKey && <BrandMark brand={cardBrandKey} alt={cardBrandLabel} height={14} />}
          <span>{cardLast4 ? `•••• ${cardLast4}` : "Card"}</span>
        </div>
      </div>

      <div style={{ borderRadius: 14, ...PREMIUM_PANEL, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
          <span>PCoins credited</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: GREEN, fontWeight: 700 }}>
            <Icon name="pnrcoin" size={14} style={{ color: GREEN }} />+{fmtInt(creditedCoins)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
          <span>Free PCash added</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#fff", fontWeight: 700 }}>
            <Icon name="pnrcash" size={14} style={{ color: "#E4E4E7" }} />+{fmtUsd(creditedCash)}
          </span>
        </div>
        {showBonus && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>
            <span>First-purchase bonus</span>
            <span style={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>+{fmtInt(bonusCoins)} · +{fmtUsd(bonusCash)}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: "1px solid #1F1F23" }}>
          <span style={{ font: "700 14px 'Outfit'" }}>New balance</span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, font: "800 17px 'Manrope'", color: GREEN }}>
              <Icon name="pnrcoin" size={15} style={{ color: GREEN }} />{Number(balUser.coins || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, font: "800 14px 'Manrope'", color: "#fff" }}>
              <Icon name="pnrcash" size={14} style={{ color: "#E4E4E7" }} />{fmtUsd(balUser.cash)}
            </span>
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 10 }}>
        <button type="button" onClick={() => { onClose?.(); navigate("/wallet"); }} data-testid="checkout-view-wallet"
          style={{ height: 54, borderRadius: 14, background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", display: "flex", alignItems: "center", justifyContent: "center", font: "700 15px 'Outfit'", color: "#fff", border: "none", cursor: "pointer" }}>
          View wallet
        </button>
        <button type="button" onClick={() => { onClose?.(); navigate("/"); }} data-testid="checkout-start-playing"
          style={{ height: 54, borderRadius: 14, background: GREEN, color: CO, display: "flex", alignItems: "center", justifyContent: "center", font: "800 16px 'Manrope'", border: "none", cursor: "pointer" }}>
          Start playing
        </button>
      </div>
      <span style={{ textAlign: "center", font: "500 11px 'Outfit'", color: "#71717a" }}>
        Receipt sent to {balUser.email || "your email"}{intent?.payment_intent_id ? ` · ${refShort(intent.payment_intent_id)}` : ""}
      </span>
    </div>
  );

  const body =
    step === "method" ? methodStep :
    step === "pay" ? payStep :
    step === "failed" ? failedStep :
    successStep;

  // ---------------------------------- framing (portal) ----------------------------------
  return ReactDOM.createPortal(
    <div data-testid="checkout-sheet" role="dialog" aria-modal="true" aria-label="Checkout"
      style={{ position: "fixed", inset: 0, zIndex: 100, fontFamily: "'Outfit',sans-serif" }}>
      {/* dimmed + blurred backdrop */}
      <div onClick={onClose}
        style={{
          position: "absolute", inset: 0, background: "rgba(5,5,6,.74)",
          backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        }} />

      {/* Desktop: centered 520px modal (lg+) */}
      <div className="pnr-checkout-desktop" style={{ position: "absolute", inset: 0, display: "none", alignItems: "center", justifyContent: "center", padding: 24, pointerEvents: "none" }}>
        <div onClick={(e) => e.stopPropagation()}
          style={{
            width: 520, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto",
            boxSizing: "border-box", padding: 24, borderRadius: 24,
            background: "linear-gradient(145deg, #0E0E12 0%, #0C0C0E 55%, #090909 100%)",
            border: "1px solid #27272A", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07), 0 40px 80px -20px rgba(0,0,0,.85)", pointerEvents: "auto",
          }}>
          {body}
        </div>
      </div>

      {/* Mobile: bottom sheet (< lg) */}
      <div className="pnr-checkout-mobile" style={{ position: "absolute", inset: 0 }}>
        <div onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0, boxSizing: "border-box",
            padding: "8px 16px 34px", borderRadius: "28px 28px 0 0",
            background: "linear-gradient(145deg, #0E0E12 0%, #0C0C0E 55%, #090909 100%)",
            borderTop: "1px solid #27272A", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07)",
            maxHeight: "94vh", overflowY: "auto",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
          <span style={{ alignSelf: "center", width: 40, height: 5, borderRadius: 999, background: "#3F3F46", flex: "none" }} />
          {body}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---- helpers ----
function radioStyle(selected) {
  return selected
    ? { width: 20, height: 20, flex: "none", marginLeft: "auto", borderRadius: "50%", boxSizing: "border-box", border: "6px solid #00D632" }
    : { width: 20, height: 20, flex: "none", marginLeft: "auto", borderRadius: "50%", boxSizing: "border-box", border: "1.5px solid #3F3F46" };
}

function greenCta(height, font, withGap = false, _icon) {
  return {
    height, borderRadius: 14, background: "#00D632", color: "#0A0A0A",
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: withGap ? 8 : 0, font, letterSpacing: "-.015em", border: "none", cursor: "pointer", width: "100%",
  };
}

function methodName(key) {
  const m = { "apple-pay": "Apple Pay", "google-pay": "Google Pay", paypal: "PayPal", venmo: "Venmo", crypto: "Crypto" };
  return m[key] || "This method";
}

function refShort(id) {
  const tail = String(id || "").replace(/[^a-zA-Z0-9]/g, "").slice(-5).toUpperCase();
  return `Order PNR-${tail || "00000"}`;
}
